import { useLayoutEffect, useRef, useState, type ReactNode, type DragEvent } from 'react';
import {
  makeStyles,
  mergeClasses,
  tokens,
  SpinButton,
  Label,
  Text,
  type SpinButtonChangeEvent,
  type SpinButtonOnChangeData,
} from '@coreai/fluentui-react';
import { EndActionsSlot } from '../shared/EndActionsSlot';
import { TokenizedInput } from './TokenizedInput';
import { ExpressionPreview } from './ExpressionPreview';
import { useFieldPreviewFocus } from './useFieldPreviewFocus';
import type { AgentParameterSchema } from './AgentParameterMenu';
import {
  type ValueSegment,
  ValueSegmentType,
  TokenType,
  createLiteralValueSegment,
  createTokenValueSegment,
  isValueSegmentArray,
  segmentsToString,
  hasTokens,
  type Token,
} from '@/types/valueSegment';
import { wrapAsExpression } from '@/utils/expressions';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  labelRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalS,
  },
  label: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground1,
  },
  required: {
    color: tokens.colorPaletteRedForeground1,
    marginLeft: tokens.spacingHorizontalXXS,
  },
  inputWrapper: {
    position: 'relative',
  },
  // Bare SpinButton has no built-in DnD affordance; paint our own.
  dropTargetActive: {
    outline: `2px solid ${tokens.colorBrandStroke1}`,
    outlineOffset: '2px',
    borderRadius: tokens.borderRadiusMedium,
  },
  input: {
    width: '100%',
  },
  error: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorPaletteRedForeground1,
  },
});

export interface NumberFieldProps {
  name: string;
  label: string;
  /**
   * Plain numeric values render as a `SpinButton`. Strings containing
   * expression markers (`${...}`, `@{...}`, `@func(...)`) and
   * {@link ValueSegment} arrays render as a tokenized input so dynamic
   * content inserted via the field-fork popover round-trips correctly.
   */
  value: number | string | ValueSegment[] | undefined;
  onChange: (value: number | ValueSegment[] | undefined) => void;
  description?: string | undefined;
  placeholder?: string | undefined;
  required?: boolean | undefined;
  disabled?: boolean | undefined;
  error?: string | undefined;
  min?: number | undefined;
  max?: number | undefined;
  step?: number | undefined;
  /** When true, parses input as an integer (rounds, rejects fractional input). */
  isInteger?: boolean | undefined;
  /** Slot rendered inside the input's right edge, offset to clear the spinner arrows. */
  endActions?: ReactNode | undefined;
  /** Forwarded to the tokenized input fallback when dynamic content is present. */
  showAgentParameterButton?: boolean | undefined;
  agentParameterSchema?: AgentParameterSchema | undefined;
  actionOutputs?: Record<string, unknown> | undefined;
  hasRunData?: boolean | undefined;
  isStale?: boolean | undefined;
}

const DEFAULT_PLACEHOLDER = 'Enter number...';

const ACCEPTED_DRAG_TYPES = [
  'application/json',
  'application/x-otto-expression',
  'text/plain',
] as const;

// Narrower than tokenParsingService.containsExpression: a literal word like
// "outputs" must stay on the SpinButton path — only proper expression syntax
// (`${...}`, `@{...}`, `@func(`) promotes to the tokenized editor.
const EXPRESSION_MARKER_RE = /\$\{|@\{|@[a-zA-Z_]\w*\(/;
const hasExpressionMarkers = (raw: string): boolean =>
  raw.length >= 3 && EXPRESSION_MARKER_RE.test(raw);

const isDynamicValue = (value: NumberFieldProps['value']): boolean => {
  if (value == null || typeof value === 'number') return false;
  if (typeof value === 'string') return hasExpressionMarkers(value);
  if (isValueSegmentArray(value)) {
    return (
      hasTokens(value) ||
      value.some(
        (s) => s.type === ValueSegmentType.LITERAL && hasExpressionMarkers(s.value),
      )
    );
  }
  return false;
};

// Coerce string/number values into a finite number for the SpinButton, or
// `undefined` to render the placeholder. Bare numeric strings (e.g. "42"
// freshly loaded from a workflow) are accepted; everything else opts out.
const toNumber = (value: NumberFieldProps['value']): number | undefined => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
};

// `DataTransfer.types` is typed as `readonly string[]` but at runtime is a
// `DOMStringList` in older browsers — which exposes `contains()` instead of
// `includes()`. Coerce to a real array so cross-browser DnD doesn't throw
// and skip `preventDefault()`, which would break the drop entirely.
const dragTypesInclude = (types: DataTransfer['types'], wanted: readonly string[]): boolean => {
  const arr = Array.from(types as ArrayLike<string>);
  return wanted.some((t) => arr.includes(t));
};

// Map a drag payload to a value the field can actually render. Order mirrors
// the field-fork popover's emission priority: structured token JSON >
// expression template > plain text. Plain text is only accepted as a number
// or an expression — free-form strings would land as segments that the
// SpinButton can't display and `isDynamicValue` doesn't treat as dynamic,
// leaving the field visually empty with stale state in the parent.
const dropPayloadFromDataTransfer = (
  dt: DataTransfer,
): number | ValueSegment[] | null => {
  const json = dt.getData('application/json');
  if (json) {
    try {
      const token = JSON.parse(json) as Token;
      // Resolve tokenType once so the synthesized fallback expression and
      // the persisted token agree — otherwise older drag sources that omit
      // `tokenType` would emit `${undefined('name')}`.
      const tokenType = token.tokenType ?? TokenType.OUTPUTS;
      const value =
        token.value && token.value.length > 0
          ? token.value
          : wrapAsExpression(`${tokenType}('${token.name ?? token.title}')`);
      return [createTokenValueSegment({ ...token, value, tokenType }, value)];
    } catch {
      // malformed JSON — fall through to other MIME types
    }
  }

  const expression = dt.getData('application/x-otto-expression');
  if (expression) return [createLiteralValueSegment(wrapAsExpression(expression))];

  const text = dt.getData('text/plain').trim();
  if (text === '') return null;
  const numeric = Number(text);
  if (Number.isFinite(numeric)) return numeric;
  if (hasExpressionMarkers(text)) return [createLiteralValueSegment(text)];
  return null;
};

/**
 * NumberField - numeric input rendered as a Fluent UI `SpinButton`, matching
 * the styling/behaviour of `RecurrenceField`'s interval control. When the
 * value contains an expression or dynamic-content tokens it falls back to a
 * `TokenizedInput` so the field accepts and round-trips dynamic content
 * inserted via the field-fork popover.
 */
export const NumberField = ({
  name,
  label,
  value,
  onChange,
  description,
  placeholder,
  required,
  disabled,
  error,
  min,
  max,
  step = 1,
  isInteger,
  endActions,
  showAgentParameterButton,
  agentParameterSchema,
  actionOutputs,
  hasRunData,
  isStale,
}: NumberFieldProps) => {
  const styles = useStyles();
  const { isFocused, containerHandlers, previewHandlers, previewToggleSlotRef } = useFieldPreviewFocus();
  // Hooks are declared above the dynamic/SpinButton branch so call order stays
  // stable across re-renders that swap input modes (Rules of Hooks).
  const [isDragOver, setIsDragOver] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const pendingFocusRef = useRef(false);

  // After a swap-triggering onChange we re-focus the freshly-mounted input so
  // typing isn't interrupted. External prop changes don't set the flag and so
  // don't steal focus.
  useLayoutEffect(() => {
    if (!pendingFocusRef.current) return;
    pendingFocusRef.current = false;
    const focusable = containerRef.current?.querySelector<HTMLElement>(
      'input:not([type="hidden"]), [contenteditable="true"]',
    );
    if (!focusable) return;
    focusable.focus();
    if ('setSelectionRange' in focusable) {
      const input = focusable as HTMLInputElement;
      try {
        input.setSelectionRange(input.value.length, input.value.length);
      } catch {
        // input type (e.g. number) doesn't support selection — focus alone is fine
      }
    }
  });

  const setValueKeepingFocus = (next: number | ValueSegment[] | undefined) => {
    pendingFocusRef.current = true;
    onChange(next);
  };

  // Dynamic path: render TokenizedInput when the value carries expression
  // markers or token segments. Plain numeric strings stay on SpinButton.
  if (isDynamicValue(value)) {
    const segments: ValueSegment[] = isValueSegmentArray(value)
      ? value
      : [createLiteralValueSegment(typeof value === 'string' ? value : String(value))];

    const stringValue = segmentsToString(segments);

    const handleSegmentsChange = (next: ValueSegment[]) => {
      if (next.length === 0) {
        onChange(undefined);
        return;
      }
      // If the user edited the content back down to a plain numeric literal,
      // coerce to a number so serialization matches the declared type.
      const joined = segmentsToString(next);
      const stillDynamic =
        next.some((s) => s.type === ValueSegmentType.TOKEN) || hasExpressionMarkers(joined);
      if (!stillDynamic) {
        const trimmed = joined.trim();
        if (trimmed === '') {
          onChange(undefined);
          return;
        }
        const parsed = Number(trimmed);
        if (Number.isFinite(parsed) && (!isInteger || Number.isInteger(parsed))) {
          setValueKeepingFocus(isInteger ? Math.round(parsed) : parsed);
          return;
        }
      }
      onChange(next);
    };

    return (
      <div ref={containerRef} className={styles.container} {...containerHandlers}>
        <div className={styles.labelRow}>
          <Label className={styles.label} htmlFor={name}>
            {label}
            {required && <span className={styles.required}>*</span>}
          </Label>
          <EndActionsSlot previewToggleSlotRef={previewToggleSlotRef}>{endActions}</EndActionsSlot>
        </div>

        <div className={styles.inputWrapper}>
          <TokenizedInput
            name={name}
            value={segments}
            onChange={handleSegmentsChange}
            placeholder={placeholder || description || DEFAULT_PLACEHOLDER}
            multiline={false}
            disabled={disabled}
            readonly={false}
            showAgentParameterButton={showAgentParameterButton}
            agentParameterSchema={agentParameterSchema}
            actionOutputs={actionOutputs}
          />

          {isFocused && stringValue && (
            <ExpressionPreview
              value={stringValue}
              actionOutputs={actionOutputs}
              hasRunData={hasRunData}
              isStale={isStale}
              mode="overlay"
              {...previewHandlers}
            />
          )}
        </div>

        {error && (
          <Text className={styles.error} role="alert">
            {error}
          </Text>
        )}
      </div>
    );
  }

  // SpinButton path. Parse user-typed text into one of:
  // `undefined` = empty (clear), `null` = mid-typing (don't clobber prior
  // value), `number` = commit. `Number` over `parseFloat` so trailing junk
  // fails (`"1.5abc"` → NaN) but scientific notation works.
  const parse = (raw: string): number | null | undefined => {
    const trimmed = raw.trim();
    if (trimmed === '') return undefined;
    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed)) return null;
    if (isInteger && !Number.isInteger(parsed)) return null;
    return parsed;
  };

  const handleSpinChange = (_: SpinButtonChangeEvent, data: SpinButtonOnChangeData) => {
    if (data.value !== undefined && data.value !== null) {
      onChange(isInteger ? Math.round(data.value) : data.value);
      return;
    }
    if (data.displayValue !== undefined) {
      const next = parse(data.displayValue);
      if (next !== null) onChange(next);
    }
  };

  // SpinButton steps from its internal floor (`-Number.MAX_VALUE`) when value
  // is null/undefined. Seed to 0 with empty display so the placeholder shows
  // and the spinner arrows start from zero.
  const numericValue = toNumber(value);
  const isEmpty = numericValue === undefined;

  // Without these handlers a drop falls through to the browser's default
  // contenteditable behaviour: the dragged text appears in the SpinButton
  // momentarily and then vanishes when React re-applies the controlled value
  // on blur. Accepting the drop here emits a ValueSegment[] that promotes the
  // field to the TokenizedInput branch on the next render.
  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    if (disabled) return;
    if (!dragTypesInclude(e.dataTransfer.types, ACCEPTED_DRAG_TYPES)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    if (!isDragOver) setIsDragOver(true);
  };

  const handleDragLeave = () => {
    if (isDragOver) setIsDragOver(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    if (disabled) return;
    e.preventDefault();
    setIsDragOver(false);
    const next = dropPayloadFromDataTransfer(e.dataTransfer);
    if (next === null) return;
    setValueKeepingFocus(
      typeof next === 'number' && isInteger ? Math.round(next) : next,
    );
  };

  return (
    <div ref={containerRef} className={styles.container}>
      <div className={styles.labelRow}>
        <Label className={styles.label} htmlFor={name}>
          {label}
          {required && <span className={styles.required}>*</span>}
        </Label>
        <EndActionsSlot previewToggleSlotRef={previewToggleSlotRef}>{endActions}</EndActionsSlot>
      </div>

      <div
        className={mergeClasses(styles.inputWrapper, isDragOver && styles.dropTargetActive)}
        data-testid={`number-field-drop-zone-${name}`}
        onDragOver={handleDragOver}
        onDragEnter={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <SpinButton
          id={name}
          className={styles.input}
          value={isEmpty ? 0 : numericValue}
          {...(isEmpty && { displayValue: '' })}
          onChange={handleSpinChange}
          placeholder={placeholder || description || DEFAULT_PLACEHOLDER}
          disabled={disabled}
          {...(min !== undefined && { min })}
          {...(max !== undefined && { max })}
          step={step}
          aria-invalid={!!error}
        />
      </div>

      {error && (
        <Text className={styles.error} role="alert">
          {error}
        </Text>
      )}
    </div>
  );
};

NumberField.displayName = 'NumberField';
